import { Lozenge } from "@/domains/common/components/lozenge"
import { CheckmarkCircleIcon, CloseCircleIcon } from "@/domains/icons"
import { cn } from "@/domains/library"

import type { FeeTier } from "../../constants"
import type { PoolOption } from "../../stores/create-pool-store"

export interface FeeTierSelectorViewProps {
  feeTiers: Array<FeeTier>
  selectedFeeTier: number | undefined
  poolOptions: Array<PoolOption>
  onFeeTierSelect: (feeTier: number) => void
}

/**
 * Convert fee tier (basis points) to basis points display
 * e.g., 500 -> "5 bps", 10000 -> "100 bps"
 */
function feeTierToBps(feeTier: number): string {
  return `${feeTier / 100} bps`
}

/**
 * Get descriptive name for fee tier (like Shadow's "Stable", "Bluechip", etc.)
 */
function getFeeTierName(feeTier: number): string {
  switch (feeTier) {
    case 100:
      return "Ultra Stable"
    case 500:
      return "Stable"
    case 3000:
      return "Bluechip"
    case 10000:
      return "Volatile"
    default:
      return "Custom"
  }
}

/**
 * Pure View component for fee tier selection
 * Displays all fee tiers as a visible series of buttons (like Shadow/Aerodrome)
 * Shows percentage and basis points
 * Mobile-friendly: wraps on small screens
 * Stateless - all data passed via props
 * Storybook-ready
 */
export function FeeTierSelectorView({
  feeTiers,
  selectedFeeTier,
  poolOptions,
  onFeeTierSelect,
}: FeeTierSelectorViewProps) {
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="text-sm font-medium text-t100">Fee Tier</div>
      <div className="grid grid-cols-4 gap-2">
        {feeTiers.map((tier) => {
          const isSelected = tier.key === selectedFeeTier
          const poolOption = poolOptions.find((opt) => opt.feeTier.key === tier.key)
          const poolExists = poolOption?.status === "existing" || poolOption?.status === "low-liquidity"
          const canCreate = poolOption?.status === "new"

          return (
            <button
              key={tier.key}
              type="button"
              onClick={() => onFeeTierSelect(tier.key)}
              disabled={poolExists}
              className={cn(
                "group relative flex w-full flex-col items-start justify-center gap-1 rounded-lg border px-4 py-3 text-left transition-all",
                poolExists
                  ? "cursor-not-allowed border-p400-light bg-p400/50 opacity-60"
                  : isSelected
                    ? "border-p100 bg-p300-light shadow-sm hover:border-p100 hover:bg-p300-light"
                    : "border-p400-light bg-p400 hover:border-p100 hover:bg-p300-light",
              )}
            >
              {/* Status icon top right */}
              {canCreate && (
                <div className="absolute right-2 top-2">
                  <CheckmarkCircleIcon size={16} className="text-success-text" />
                </div>
              )}
              {poolExists && (
                <div className="absolute right-2 top-2">
                  <CloseCircleIcon size={16} className="text-error-text" />
                </div>
              )}

              {/* Top line: Name (like Shadow) */}
              <span className={cn("text-xs font-medium", isSelected ? "text-t100" : "text-t200")}>
                {getFeeTierName(tier.key)}
              </span>

              {/* Middle line: Percentage */}
              <div className="flex items-center gap-2">
                <span className={cn("text-sm font-semibold", isSelected ? "text-t100" : "text-t200")}>
                  {tier.value}
                </span>
              </div>

              {/* Bottom line: Basis points and status lozenge */}
              <div className="flex w-full items-center justify-between">
                <span className={cn("text-xs", isSelected ? "text-t200" : "text-t300")}>{feeTierToBps(tier.key)}</span>
                {canCreate && (
                  <Lozenge color="success" size="xxs" className="absolute bottom-2 right-2">
                    ok
                  </Lozenge>
                )}
                {poolExists && (
                  <Lozenge color="error" size="xxs" className="absolute bottom-2 right-2">
                    exists
                  </Lozenge>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
